import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { PublicLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardFooter } from "@/components/ui/card";
import { Loader2, Lock, AlertTriangle, Check, X } from "lucide-react";
import { z } from "zod";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { apiFetch } from "@/lib/api";
import { PasswordStrengthMeter } from "@/components/password-strength-meter";

const MIN_LENGTH = 8;

const schema = z.object({
  password: z.string()
    .min(MIN_LENGTH, `Password must be at least ${MIN_LENGTH} characters`)
    .regex(/[A-Z]/, "Must contain at least one uppercase letter")
    .regex(/[0-9]/, "Must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Must contain at least one special character"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

function PolicyRow({ met, label }: { met: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {met ? (
        <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />
      ) : (
        <X className="w-3.5 h-3.5 text-white/30 shrink-0" />
      )}
      <span className={met ? "text-green-400" : "text-white/40"}>{label}</span>
    </div>
  );
}

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      setTokenError(true);
    } else {
      setToken(t);
    }
  }, []);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirmPassword: "" },
    mode: "onChange",
  });

  const passwordValue = useWatch({ control: form.control, name: "password" });
  const confirmValue = useWatch({ control: form.control, name: "confirmPassword" });

  const meetsLength = passwordValue.length >= MIN_LENGTH;
  const hasUppercase = /[A-Z]/.test(passwordValue);
  const hasNumber = /[0-9]/.test(passwordValue);
  const hasSpecial = /[^A-Za-z0-9]/.test(passwordValue);

  const passwordsMatch = passwordValue.length > 0 && confirmValue.length > 0 && passwordValue === confirmValue;
  const passwordsMismatch = confirmValue.length > 0 && passwordValue !== confirmValue;

  async function onSubmit(values: z.infer<typeof schema>) {
    if (!token) return;
    try {
      setIsSubmitting(true);
      setApiError(null);
      await apiFetch("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, password: values.password }),
      });
      setSuccess(true);
      setTimeout(() => setLocation("/login?reset=success"), 2500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      if (msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("already")) {
        setTokenError(true);
      } else {
        setApiError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (tokenError) {
    return (
      <PublicLayout>
        <SEOHead title="Reset Password — OPEN MAT" description="Reset your password." canonicalPath="/reset-password" noindex />
        <div className="flex-1 flex items-center justify-center p-4 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
          <Card className="w-full max-w-md glass-panel border-white/10 z-10 relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-destructive to-transparent" />
            <CardHeader className="text-center pb-8 space-y-1">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mb-4">
                <AlertTriangle className="w-7 h-7 text-destructive" />
              </div>
              <h1 className="text-3xl font-display font-semibold uppercase tracking-wider text-white leading-none">Link Expired</h1>
              <CardDescription className="text-muted-foreground">
                This reset link is invalid, expired, or has already been used.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Reset links are single-use and expire after <span className="text-white font-medium">30 minutes</span>.
              </p>
              <Link href="/forgot-password">
                <Button className="w-full h-12 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-xl">
                  Request a new link
                </Button>
              </Link>
            </CardContent>
            <CardFooter className="flex justify-center border-t border-white/5 pt-6 mt-2">
              <p className="text-sm text-muted-foreground">
                Remembered it?{" "}
                <Link href="/login">
                  <span className="text-primary hover:underline font-semibold cursor-pointer">Sign in</span>
                </Link>
              </p>
            </CardFooter>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  if (success) {
    return (
      <PublicLayout>
        <SEOHead title="Reset Password — OPEN MAT" description="Reset your password." canonicalPath="/reset-password" noindex />
        <div className="flex-1 flex items-center justify-center p-4 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
          <Card className="w-full max-w-md glass-panel border-white/10 z-10 relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
            <CardHeader className="text-center pb-8 space-y-1">
              <h1 className="text-2xl font-display font-semibold uppercase tracking-wider text-white leading-none">Password Updated!</h1>
              <CardDescription className="text-muted-foreground">Redirecting you to login…</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEOHead title="Reset Password — OPEN MAT" description="Set a new password for your OPEN MAT account." canonicalPath="/reset-password" noindex />
      <div className="flex-1 flex items-center justify-center p-4 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />

        <Card className="w-full max-w-md glass-panel border-white/10 z-10 relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
          <CardHeader className="space-y-1 pb-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
              <Lock className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-3xl font-display font-semibold uppercase tracking-wider text-white leading-none">Set New Password</h1>
            <CardDescription className="text-muted-foreground">Choose a strong password for your account.</CardDescription>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <Label className="text-white/80">New password</Label>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className="bg-black/50 border-white/10 focus-visible:ring-primary focus-visible:border-primary text-white h-12 rounded-xl"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                      {passwordValue.length > 0 && (
                        <div className="mt-2 p-3 rounded-lg bg-white/5 border border-white/8 space-y-2 text-xs">
                          <PasswordStrengthMeter password={passwordValue} />
                          <div className="space-y-1.5">
                            <PolicyRow met={meetsLength}  label={`At least ${MIN_LENGTH} characters`} />
                            <PolicyRow met={hasUppercase} label="At least one uppercase letter" />
                            <PolicyRow met={hasNumber}    label="At least one number" />
                            <PolicyRow met={hasSpecial}   label="At least one special character (!@#$…)" />
                          </div>
                        </div>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <Label className="text-white/80">Confirm new password</Label>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className={`bg-black/50 border-white/10 focus-visible:ring-primary focus-visible:border-primary text-white h-12 rounded-xl transition-colors ${
                            passwordsMatch ? "border-green-500/50 focus-visible:border-green-500 focus-visible:ring-green-500" :
                            passwordsMismatch ? "border-red-500/50 focus-visible:border-red-500 focus-visible:ring-red-500" : ""
                          }`}
                          {...field}
                        />
                      </FormControl>
                      {confirmValue.length > 0 && (
                        <div className={`flex items-center gap-1.5 text-xs mt-1 ${passwordsMatch ? "text-green-400" : "text-red-400"}`}>
                          {passwordsMatch ? (
                            <><Check className="w-3.5 h-3.5" /> Passwords match</>
                          ) : (
                            <><X className="w-3.5 h-3.5" /> Passwords do not match</>
                          )}
                        </div>
                      )}
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                {apiError && (
                  <p className="text-sm text-destructive text-center">{apiError}</p>
                )}
                <Button
                  type="submit"
                  className="w-full h-12 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-xl mt-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update Password"}
                </Button>
              </form>
            </Form>
          </CardContent>

          <CardFooter className="flex justify-center border-t border-white/5 pt-6 mt-2">
            <p className="text-sm text-muted-foreground">
              Remembered it?{" "}
              <Link href="/login">
                <span className="text-primary hover:underline font-semibold cursor-pointer">Sign in</span>
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </PublicLayout>
  );
}
